/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment */
import { useState } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Button, Spinner } from "react-bootstrap";
import { WebLayout } from "../components/web-layout";
import { SessionParams, useLoginMutation } from "./queries";
import LabelledField from "../profile/components/labelled-field";
import { useBranding, useClientId } from "../../contexts/branding";
import { Spacer } from "../components/spacer";
import { fetchAppConfig } from "../../hooks/use-config";
import LoginWithCode from "./otp";
import { PasswordField } from "../../components/password-field";
import { CURRENT_URL } from "../../constants";

interface Props {
    onSuccess: (url: string) => void;
}

export default function Form({ onSuccess }: Props) {
    const clientId = useClientId();
    const [otpLogin, setOtpLogin] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [userEmail, setUserEmail] = useState<string>("");
    const [redirectPathName, setRedirectPathName] = useState<string>("");
    const { t } = useTranslation();
    const { label } = useBranding();
    const navigate = useNavigate();
    const { hash, state } = useLocation();
    const emailShema = yup
        .string()
        .email(t("Please enter a valid email Id"))
        .required(t("Email is required"));
    const schema = yup.object().shape({
        email: emailShema,
        password: yup.string().required(t("Please enter a valid password")),
    });
    const otpSchema = yup.object().shape({ email: emailShema });
    const { mutateAsync: login } = useLoginMutation();
    const {
        register,
        handleSubmit,
        setError,
        getValues,
        formState: { errors },
    } = useForm<SessionParams>({
        resolver: yupResolver(schema),
    });

    const onSubmit = ({ email, password }: Omit<SessionParams, "clientId">) => {
        setSubmitting(true);
        login({ email, password, clientId })
            .then(() => setSubmitting(false))
            .then(() => fetchAppConfig())
            .then((data) => {
                const currentUrl = localStorage.getItem(CURRENT_URL);
                let redirectUrl;
                if (state?.from?.pathname) {
                    redirectUrl = state?.from?.pathname;
                } else if (currentUrl) {
                    redirectUrl = currentUrl;
                } else if (data?.nav_menu && data.nav_menu[0]?.url) {
                    redirectUrl = data.nav_menu[0].url;
                } else {
                    redirectUrl = "/app/dashboard";
                }
                onSuccess(redirectUrl);
                localStorage.removeItem(CURRENT_URL);
            })
            .catch((error: Error) => {
                setSubmitting(false);
                const { message } = error;
                if (message.startsWith("Password Incorrect!!")) {
                    setError("password", { type: "custom", message });
                } else if (message.includes("locked out")) {
                    setError("email", { type: "custom", message });
                } else if (message.startsWith("Email is incorrect!!")) {
                    setError("email", { type: "custom", message });
                } else {
                    setErrorMessage(message);
                }
            });
    };

    const onOTPLogin = () => {
        const newLocation = "#login_with_code";
        otpSchema
            .validate({ email: getValues("email") })
            .then(() => {
                if (hash !== newLocation) {
                    navigate(newLocation);
                }
                setRedirectPathName(state?.from?.pathname);
                setUserEmail(getValues("email"));
                setOtpLogin(true);
            })
            .catch((err) =>
                setError("email", {
                    message: err.errors.join(","),
                })
            );
    };

    if (otpLogin) {
        return (
            <LoginWithCode
                email={userEmail}
                backToLogin={() => setOtpLogin(false)}
                redirectPathName={redirectPathName}
            />
        );
    }

    return (
        <WebLayout>
            <Row>
                <Col
                    xs={12}
                    md={{ offset: 2, span: 8 }}
                    lg={{ offset: 2, span: 8 }}
                >
                    <LabelledField
                        name="email"
                        className="login-label"
                        label={t("email_id")}
                        errorMessage={errors.email?.message}
                    >
                        <input
                            {...register("email")}
                            type="text"
                            className="form-control field-height mt-2"
                            placeholder={t("email_placeholder")}
                        />
                    </LabelledField>
                    <PasswordField
                        name="password"
                        label={t("Password")}
                        errorMessage={errors.password?.message}
                        register={register}
                        placeholder={t("password_placeholder")}
                    />
                    {errorMessage && (
                        <Row className="mt-2">
                            <Col xs={12}>
                                <span className="text-danger">
                                    {t(errorMessage)}
                                </span>
                            </Col>
                        </Row>
                    )}
                    <Spacer />
                    <div className="text-right m-b-20">
                        <Link
                            className={label !== "HabitNu" ? "ada-link" : ""}
                            to="/forgot-password"
                        >
                            {t("Forgot password?")}
                        </Link>
                    </div>
                    <div className="align-centre">
                        <Button
                            variant="success"
                            className={`w-100 field-height ${
                                label !== "HabitNu" ? "ada-btn-success" : ""
                            }`}
                            disabled={submitting}
                            onClick={handleSubmit(onSubmit)}
                        >
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" />
                                    <span className="ml-2">
                                        {t("Signing in")}
                                    </span>
                                </>
                            ) : (
                                <span>{t("Sign in")}</span>
                            )}
                        </Button>
                        <div className="d-flex align-items-center btn-divider">
                            <hr className="flex-grow-1" />
                            <div className="divider-text">{t("login.or")}</div>
                            <hr className="flex-grow-1" />
                        </div>

                        <Button
                            variant="outline-success"
                            className={`w-100 field-height ${
                                label !== "HabitNu"
                                    ? "ada-btn-success-outlined"
                                    : ""
                            }`}
                            disabled={submitting}
                            onClick={onOTPLogin}
                        >
                            <span>{t("login_with_otp")}</span>
                        </Button>
                    </div>
                </Col>
            </Row>
        </WebLayout>
    );
}
